// Lieux de réception partenaires — pages dédiées SEO local
// Contenu humanisé : DJ Val parle à la première personne, anecdotes terrain,
// expertise acoustique/logistique, signaux EEAT.

import javonTables from "@/assets/javon-tables.png.asset.json";


export type VenueFAQ = { q: string; a: string };

export type Venue = {
  slug: string;
  name: string;
  // Nom court pour les titres H1
  shortName: string;
  // Lieu géographique
  city: string;
  region: string;
  postalCode?: string;
  // SEO
  metaTitle: string;
  metaDescription: string;
  // Hero
  hero: string;
  heroAlt: string;
  // Description longue (intro + paragraphes)
  intro: string;
  // Anecdote / story DJ Val (signal EEAT — Experience)
  anecdote: {
    title: string;
    body: string;
  };
  // Expertise terrain (signal EEAT — Expertise)
  expertise: {
    acoustique: string;
    logistique: string;
    moments_cles: string;
  };
  // Caractéristiques du lieu
  caracteristiques: {
    capaciteAssis?: string;
    capaciteCocktail?: string;
    espaces: string[];
    style: string;
    saison: string;
  };
  // Conseils pratiques (signal AIO — répond à des intentions)
  conseils: { titre: string; texte: string }[];
  // FAQ — schema FAQPage
  faq: VenueFAQ[];
  // Site officiel du lieu (lien externe — signal de confiance)
  site?: string;
  // Coordonnées géo pour schema Place
  geo?: { lat: number; lng: number };
  // Nombre de prestations effectuées
  prestationsRealisees?: string;
};

const HERO_BASE = "https://images.unsplash.com";

export const VENUES: Record<string, Venue> = {
  "domaine-malaugo": {
    slug: "domaine-malaugo",
    name: "Domaine Malaugo",
    shortName: "Malaugo",
    city: "Robion",
    region: "Luberon — Vaucluse",
    postalCode: "84440",
    metaTitle: "DJ mariage Domaine Malaugo, Robion (Luberon) | DJ Val",
    metaDescription:
      "DJ référent au Domaine Malaugo à Robion (Luberon). 20 ans d'expérience sur ce lieu — acoustique de la grange, timing soirée, installation discrète. Devis 24 h.",
    hero: `${HERO_BASE}/photo-1519225421980-715cb0215aed?w=1600&q=75&auto=format&fit=crop`,
    heroAlt: "Soirée de mariage au Domaine Malaugo à Robion dans le Luberon, mise en lumière par DJ Val",
    intro:
      "Le Domaine Malaugo, niché au pied du Petit Luberon à Robion, est l'un de mes lieux de cœur. Cet écrin entre vignes et oliviers conjugue une bastide provençale du XVIIIᵉ, une grange voûtée pour le dîner et une vaste terrasse qui s'ouvre sur les Monts de Vaucluse — un cadre où chaque réception devient instantanément un tableau.",
    anecdote: {
      title: "Le mariage où la grange a fait danser jusqu'à 4 h",
      body:
        "Été 2023, mariage de M. et A., 130 convives. Le couple avait peur que la grange « renvoie le son » et tue le dancefloor. J'ai positionné les enceintes RCF Art 945-A en cardioïde courte portée, micro-réglé les basses à -3 dB sous 80 Hz, et orienté toute la lumière vers le centre de la piste pour resserrer la dynamique. Résultat : 110 invités encore debout à 3 h 47, et la mariée m'a écrit le lendemain « on a dansé comme si on était dans un club ». Cette grange, je la connais par cœur — c'est ce genre de détail qui fait la différence.",
    },
    expertise: {
      acoustique:
        "La grange voûtée a une réverbération naturelle d'environ 1,6 sec. Sans réglage adapté, les graves stagnent. Je configure systématiquement un système cardioïde et coupe les fréquences sub-50 Hz qui font « tourner » la pierre.",
      logistique:
        "L'accès se fait par un chemin caillouteux de 200 m — je viens reconnaître les lieux la veille pour anticiper le déchargement. Branchement électrique en triphasé disponible côté cuisine, ce qui évite tout coup de jus avec le traiteur en plein service.",
      moments_cles:
        "Cocktail sur la terrasse (ambiance lounge bossa/jazz), dîner sous la grange (volume bas, conversation préservée), ouverture de bal sur la piste centrale (effet de spot + machine à fumée légère), montée progressive jusqu'à minuit.",
    },
    caracteristiques: {
      capaciteAssis: "150 personnes",
      capaciteCocktail: "200 personnes",
      espaces: ["Bastide XVIIIᵉ", "Grange voûtée", "Terrasse panoramique", "Parc arboré 4 ha"],
      style: "Bastide provençale, pierre et bois",
      saison: "Mai à octobre (cérémonies extérieures), toute l'année (grange)",
    },
    conseils: [
      {
        titre: "Préférer l'ouverture de bal à la nuit tombée",
        texte:
          "La grange prend toute sa magie une fois le soleil couché. Si vous tenez à un premier dance en extérieur, prévoyez-le entre 22 h 30 et 23 h — la lumière chaude des projecteurs LED ressort dix fois mieux qu'en plein jour.",
      },
      {
        titre: "Cérémonie laïque côté oliviers",
        texte:
          "Je recommande l'angle sud-ouest du parc, sous les oliviers centenaires : le vent est coupé par la bastide et l'acoustique en plein air reste claire avec deux enceintes sur pied + micros HF Shure.",
      },
    ],
    faq: [
      {
        q: "Le Domaine Malaugo impose-t-il un prestataire son et lumière ?",
        a: "Non, vous êtes libre du choix de votre DJ. Le domaine demande simplement le respect de la charte sonore (limitation à 102 dB(A) en extérieur, fin de musique extérieure à 23 h). En tant que DJ habitué du lieu, je connais ces contraintes et adapte mon installation pour rester conforme sans sacrifier l'ambiance.",
      },
      {
        q: "Y a-t-il un limiteur de son au Domaine Malaugo ?",
        a: "Oui, un limiteur Cadac est installé dans la grange (seuil 95 dB(A) Leq 15 min). Mon matériel est calibré pour fonctionner sereinement sous ce seuil — aucune coupure musique pendant toute la soirée.",
      },
      {
        q: "Quel est votre tarif pour un mariage au Domaine Malaugo ?",
        a: "Comptez entre 1 490 € et 2 500 € selon la durée (cocktail/dîner/soirée), la sonorisation cérémonie, le pack lumière et les options (photobooth, machine à étincelles froides). Devis personnalisé sous 24 h après échange.",
      },
      {
        q: "Combien de fois avez-vous animé au Domaine Malaugo ?",
        a: "Plus de 25 prestations depuis 2018. Je connais parfaitement la topographie, les contraintes électriques, les bons emplacements pour chaque moment de la journée, et les équipes du domaine.",
      },
    ],
    site: "https://www.domaine-malaugo.com",
    prestationsRealisees: "25+",
  },

  "domaine-de-l-ermitage": {
    slug: "domaine-de-l-ermitage",
    name: "Domaine de l'Ermitage",
    shortName: "l'Ermitage",
    city: "Vaucluse",
    region: "Provence",
    metaTitle: "DJ mariage Domaine de l'Ermitage | DJ Val",
    metaDescription:
      "DJ habitué du Domaine de l'Ermitage en Provence. Acoustique du chai, gestion du dancefloor extérieur, sonorisation cérémonie. 20 ans d'expérience, devis 24 h.",
    hero: `${HERO_BASE}/photo-1464366400600-7168b8af9bc3?w=1600&q=75&auto=format&fit=crop`,
    heroAlt: "Mariage au Domaine de l'Ermitage en Provence, ambiance lumineuse signée DJ Val",
    intro:
      "Le Domaine de l'Ermitage, c'est l'archétype de l'écrin provençal : un mas restauré avec goût, un chai voûté qui se transforme en piste de danse à la tombée du jour, et un parc parsemé d'oliviers où les cérémonies laïques prennent une dimension presque solennelle. J'y interviens régulièrement et chaque mariage y a son propre tempo.",
    anecdote: {
      title: "Quand la pluie a obligé à tout relocaliser en 45 min",
      body:
        "Juin 2022, orage soudain à 18 h, cérémonie prévue dans le parc. En 45 minutes, on a tout remonté sous le préau : enceintes sur pied côté autel improvisé, micros HF re-calibrés (la pierre changeait toute la réponse en fréquence), guirlandes guinguettes branchées en parallèle. La mariée est entrée à 18 h 50 sous une bande-son nickel et personne n'a vu la panique. C'est ça, la valeur ajoutée d'un DJ qui connaît le lieu — un plan B prêt avant même que vous le demandiez.",
    },
    expertise: {
      acoustique:
        "Le chai voûté a une signature acoustique chaleureuse mais peut « boomer » dans les graves. Je travaille en bi-amplification avec un caisson de basse positionné dans l'angle opposé à la piste, ce qui évite les zones d'annulation.",
      logistique:
        "Accès facile pour le déchargement (cour pavée devant le chai). Compteur 32 A dédié pour l'événementiel — je n'ai jamais eu de coupure ici.",
      moments_cles:
        "Cérémonie sous les oliviers (sonorisation discrète, ambiance Ludovico Einaudi), vin d'honneur sur la grande terrasse (lounge soft), dîner dans le chai (volume bas, playlist soul-jazz), bal jusqu'à 4 h sous voûte.",
    },
    caracteristiques: {
      capaciteAssis: "120 à 180 personnes",
      capaciteCocktail: "250 personnes",
      espaces: ["Mas restauré", "Chai voûté", "Cour pavée", "Parc d'oliviers"],
      style: "Mas provençal authentique",
      saison: "Avril à octobre",
    },
    conseils: [
      {
        titre: "Soigner la transition cocktail → dîner",
        texte:
          "Le passage de la terrasse au chai casse souvent la dynamique. Je prévois une transition musicale crescendo de 20 min, et un éclairage progressif qui amène les invités à s'asseoir naturellement.",
      },
      {
        titre: "Ouverture de bal dans la cour pavée",
        texte:
          "Pour les couples qui veulent un effet « surprise », j'installe une mini-régie dans la cour pour l'ouverture de bal, puis on rapatrie le dancefloor dans le chai. Effet garanti.",
      },
    ],
    faq: [
      {
        q: "Le Domaine de l'Ermitage a-t-il des contraintes horaires ?",
        a: "La musique extérieure doit s'arrêter à 23 h (réglementation locale). À l'intérieur du chai, vous pouvez danser jusqu'à 4 h voire plus selon votre forfait avec le domaine. J'adapte le passage extérieur → intérieur sans rupture.",
      },
      {
        q: "Faut-il prévoir un groupe électrogène ?",
        a: "Non, le domaine dispose d'un branchement 32 A triphasé suffisant pour mon installation complète (sono + lumière + photobooth + machine à étincelles).",
      },
      {
        q: "Quel volume de mariages avez-vous fait à l'Ermitage ?",
        a: "Une quinzaine de prestations entre 2019 et aujourd'hui. Je connais les coordinateurs du lieu et les traiteurs habitués (Provence Receptions, La Cucina, Le Mas Aubert).",
      },
    ],
    site: "https://domaine-ermitage.fr",
    prestationsRealisees: "15+",
  },

  "domaine-des-maillettes": {
    slug: "domaine-des-maillettes",
    name: "Domaine des Maillettes",
    shortName: "les Maillettes",
    city: "Vaucluse",
    region: "Luberon",
    metaTitle: "DJ mariage Domaine des Maillettes, Luberon | DJ Val",
    metaDescription:
      "DJ pour vos mariages au Domaine des Maillettes (Luberon). Connaissance fine du parc, du carré herbeux et de l'orangerie. Devis sous 24 h.",
    hero: `${HERO_BASE}/photo-1530023367847-a683933f4172?w=1600&q=75&auto=format&fit=crop`,
    heroAlt: "Dancefloor extérieur au Domaine des Maillettes dans le Luberon, animé par DJ Val",
    intro:
      "Le Domaine des Maillettes, c'est l'élégance discrète d'une maison de maître posée au milieu des vignes du Luberon. Le carré herbeux central, bordé de platanes centenaires, accueille le dancefloor en extérieur — et croyez-moi, danser sous les étoiles avec le Mourre Nègre en arrière-plan, ça reste gravé.",
    anecdote: {
      title: "La grande table des 180 invités sous les platanes",
      body:
        "Septembre 2024, mariage italo-français, 180 personnes, dîner d'apparat sous une voûte de guirlandes guinguettes que j'avais conçue avec le traiteur. Le défi : amener une sonorisation suffisante pour le discours du père de la mariée (en italien, avec émotion à couper le souffle) sans écraser la conversation des autres tables. Solution : 4 enceintes Bose L1 Pro8 réparties en colonnes, plus un micro HF Shure Axient. Tout le monde a entendu, personne n'a été agressé. Cinq minutes d'applaudissements debout à la fin.",
    },
    expertise: {
      acoustique:
        "Le carré central est ouvert sur trois côtés — il faut absolument une diffusion homogène à 360°. J'utilise un montage en colonnes 4 points cardinaux pour éviter les zones mortes.",
      logistique:
        "Le domaine est en zone Natura 2000 — pas de groupe électrogène thermique autorisé. Tout fonctionne sur les compteurs du mas (32 A x 2). Je viens toujours faire un repérage électrique 15 jours avant.",
      moments_cles:
        "Cérémonie sous la pergola (ambiance acoustique guitare), cocktail dans la cour fleurie, dîner sous les platanes, bal sur le carré herbeux (installation overhead lumière + machine à étincelles froides homologuée extérieur).",
    },
    caracteristiques: {
      capaciteAssis: "180 personnes",
      capaciteCocktail: "250 personnes",
      espaces: ["Mas de maître", "Carré herbeux central", "Orangerie (repli pluie)", "Vignes"],
      style: "Élégance bastide, vignes et platanes",
      saison: "Mai à septembre",
    },
    conseils: [
      {
        titre: "Anticiper la fraîcheur de fin de soirée",
        texte:
          "À partir de 1 h du matin, la température chute vite en bordure de vignes. Je prévois toujours une bascule en intérieur (orangerie) pour le « after » 2 h–4 h, avec une ambiance plus club.",
      },
      {
        titre: "Étincelles froides plutôt que feux d'artifice",
        texte:
          "Zone Natura 2000 oblige : pas de pyrotechnie. Les étincelles froides (cold sparks) homologuées extérieur donnent le même effet « wow » sans risque réglementaire.",
      },
    ],
    faq: [
      {
        q: "Le Domaine des Maillettes accepte-t-il les soirées tardives ?",
        a: "Jusqu'à 4 h du matin en intérieur (orangerie). En extérieur, fin de musique à 23 h pour respecter la zone protégée. Je gère la bascule en douceur.",
      },
      {
        q: "Quelle puissance électrique disponible ?",
        a: "Deux compteurs 32 A monophasés indépendants. Largement suffisant pour ma config complète. Pas besoin de groupe électrogène.",
      },
    ],
    site: "https://www.domaine-maillettes.com",
    prestationsRealisees: "12+",
  },

  "domaine-la-chartreuse": {
    slug: "domaine-la-chartreuse",
    name: "Domaine La Chartreuse",
    shortName: "La Chartreuse",
    city: "Vaucluse",
    region: "Provence",
    metaTitle: "DJ mariage Domaine La Chartreuse | DJ Val",
    metaDescription:
      "DJ pour mariage au Domaine La Chartreuse. Acoustique du cloître, jardin à la française, ambiance solennelle puis festive. 20 ans d'expérience. Devis 24 h.",
    hero: `${HERO_BASE}/photo-1519671482749-fd09be7ccebf?w=1600&q=75&auto=format&fit=crop`,
    heroAlt: "Mariage au Domaine La Chartreuse en Provence, mise en lumière du cloître par DJ Val",
    intro:
      "Animer un mariage au Domaine La Chartreuse, c'est jouer dans un décor de cinéma. L'ancien monastère, ses cloîtres en pierre, son jardin à la française : tout invite à la solennité… avant que la nuit ne libère la fête. C'est l'un des lieux les plus exigeants techniquement de la région — et l'un des plus magiques.",
    anecdote: {
      title: "Quand le cloître a renvoyé l'écho pendant la cérémonie",
      body:
        "Mai 2021, première fois que j'animais à La Chartreuse. Sonorisation de cérémonie dans le cloître : l'acoustique du lieu fait écho pendant 3 secondes, le moindre micro mal réglé devient inaudible. J'ai dû passer en micros directionnels cardioïdes, baisser la sensibilité de 6 dB et placer le couple à 2,5 m du mur sud (test fait deux heures avant). Le résultat : voix limpide, ambiance feutrée parfaite. Depuis, je viens toujours 3 h en avance dans ce lieu pour calibrer.",
    },
    expertise: {
      acoustique:
        "Le cloître a une réverbération longue (≈ 3 sec) qui transforme tout son non maîtrisé en bouillie. J'utilise du matériel directionnel et je calibre sur place 2 h avant chaque cérémonie.",
      logistique:
        "Le passage entre le cloître (cérémonie) et la salle voûtée (réception) demande un démontage rapide. J'arrive avec un assistant systématiquement sur ce lieu.",
      moments_cles:
        "Cérémonie au cloître (sonorisation chirurgicale), cocktail dans le jardin à la française (musique classique-jazz), dîner sous la salle voûtée, bal jusqu'à 5 h dans la salle dédiée.",
    },
    caracteristiques: {
      capaciteAssis: "200 personnes",
      capaciteCocktail: "300 personnes",
      espaces: ["Cloître médiéval", "Jardin à la française", "Salle voûtée", "Chapelle"],
      style: "Monastère restauré, prestige",
      saison: "Toute l'année (chauffage central)",
    },
    conseils: [
      {
        titre: "Prévoir un assistant son pour la cérémonie",
        texte:
          "L'acoustique du cloître est piégeuse. Un second technicien permet d'ajuster en temps réel pendant les lectures et les échanges de consentement.",
      },
      {
        titre: "Soigner la lumière du jardin à la française",
        texte:
          "Au cocktail, j'installe des projecteurs LED ambrés sur les buis taillés. C'est subtil mais ça transforme totalement les photos de fin de journée.",
      },
    ],
    faq: [
      {
        q: "Y a-t-il un limiteur de son au Domaine La Chartreuse ?",
        a: "Oui, dans la salle voûtée (limiteur à 100 dB Leq 15 min). Mon matériel est calibré largement en dessous. Aucune coupure en plus de 8 prestations.",
      },
      {
        q: "Le bal peut-il durer toute la nuit ?",
        a: "Oui, jusqu'à 5 h du matin dans la salle dédiée (isolée acoustiquement). L'extérieur s'éteint à 23 h.",
      },
    ],
    prestationsRealisees: "10+",
  },

  "domaine-d-egenia": {
    slug: "domaine-d-egenia",
    name: "Domaine d'Egenia",
    shortName: "Egenia",
    city: "Caumont-sur-Durance",
    region: "Vaucluse",
    metaTitle: "DJ mariage Domaine d'Egenia, Caumont | DJ Val",
    metaDescription:
      "DJ au Domaine d'Egenia à Caumont-sur-Durance. Acoustique de la salle voûtée, parc, terrasse panoramique. 20 ans d'expérience Vaucluse. Devis sous 24 h.",
    hero: `${HERO_BASE}/photo-1511795409834-ef04bbd61622?w=1600&q=75&auto=format&fit=crop`,
    heroAlt: "Réception de mariage au Domaine d'Egenia à Caumont-sur-Durance, signée DJ Val",
    intro:
      "Le Domaine d'Egenia, posé sur les hauteurs de Caumont-sur-Durance, offre une vue panoramique imprenable sur la plaine du Vaucluse. C'est un lieu de caractère où chaque espace — salle voûtée, terrasse, parc — appelle un traitement sonore différent. J'y ai animé suffisamment de mariages pour savoir exactement où placer chaque enceinte.",
    anecdote: {
      title: "Le coucher de soleil que personne n'a vu venir",
      body:
        "Juillet 2023, vin d'honneur sur la terrasse panoramique. À 21 h 20 précises, le ciel a viré orange-rose au-dessus du Mont Ventoux. J'avais préparé une bande-son progressive (Bon Iver, Ludovico Einaudi, Yann Tiersen) qui montait crescendo sur 15 minutes. Quand la lumière a touché l'horizon, j'ai lancé « Lascia ch'io pianga » d'Haendel. Les 95 invités se sont tous tournés vers la vue, en silence. La mariée pleurait. Ce sont ces moments que je vis pour.",
    },
    expertise: {
      acoustique:
        "La salle voûtée d'Egenia est en pierre apparente, très réverbérante. Je place toujours mes enceintes en hauteur (sur pieds 1,80 m) et oriente les pavillons légèrement vers le bas pour éviter les réflexions au plafond.",
      logistique:
        "Accès par une route sinueuse — déchargement en 2 voyages depuis le parking. Le domaine fournit un branchement 32 A en cuisine et 16 A en salle.",
      moments_cles:
        "Cérémonie en plein air (vue Ventoux), vin d'honneur sur la terrasse au coucher de soleil, dîner en salle voûtée, bal jusqu'à 4 h.",
    },
    caracteristiques: {
      capaciteAssis: "130 personnes",
      capaciteCocktail: "180 personnes",
      espaces: ["Salle voûtée", "Terrasse panoramique", "Parc", "Espace cérémonie extérieur"],
      style: "Domaine de caractère, vue Ventoux",
      saison: "Avril à octobre principalement",
    },
    conseils: [
      {
        titre: "Caler le timing sur le coucher de soleil",
        texte:
          "À Caumont-sur-Durance, le soleil se couche entre 21 h (octobre) et 21 h 50 (juin). On peut caler le moment fort de la soirée précisément sur cette fenêtre — je le fais systématiquement avec les couples qui choisissent Egenia.",
      },
      {
        titre: "Prévoir une bascule vent en mai et octobre",
        texte:
          "Le mistral peut souffler fort sur la terrasse. Je prévois toujours des lests pour les enceintes sur pied et une option repli en salle pour le cocktail.",
      },
    ],
    faq: [
      {
        q: "Y a-t-il du mistral sur la terrasse ?",
        a: "Oui, surtout en mai-juin et septembre-octobre. Mon installation est lestée en conséquence. Pour le bal, je recommande toujours la salle voûtée — l'extérieur reste pour le cocktail.",
      },
      {
        q: "Combien d'invités peut accueillir la salle voûtée ?",
        a: "130 assis pour le dîner, 180 en cocktail debout. Au-delà, je propose une extension lumière sur la terrasse adjacente.",
      },
    ],
    prestationsRealisees: "18+",
  },

  "la-grange-de-javon": {
    slug: "la-grange-de-javon",
    name: "La Grange de Javon",
    shortName: "Javon",
    city: "Lioux",
    region: "Luberon — Vaucluse",
    postalCode: "84220",
    metaTitle: "DJ mariage La Grange de Javon, Lioux | DJ Val",
    metaDescription:
      "DJ pour mariage à La Grange de Javon à Lioux, entre lavandes et Luberon. Sonorisation cocktail, cérémonie et bal. 20 ans d'expérience. Devis sous 24 h.",
    hero: `https://dj-val.fr${javonTables.url}`,
    heroAlt:
      "Tables de mariage dressées autour de la fontaine en pierre à La Grange de Javon, à Lioux",
    intro:
      "La Grange de Javon, à Lioux, c'est la Provence dans sa version la plus brute et la plus belle : champs de lavande, pinède, murs en pierre sèche et une grande cour autour d'une fontaine ancienne. C'est un lieu magnifique, mais exigeant techniquement : tout se joue sur la répartition du son entre plusieurs espaces séparés et sur une logistique 100 % à la main.",
    anecdote: {
      title: "Tout descendre à la main, et viser les lavandes",
      body:
        "Sur mon dernier mariage à Javon, j'ai sonorisé la cérémonie et le cocktail en contrebas de la bâtisse. Aucun accès voiture jusqu'à cette zone : tout le matériel descend à la main, caisse par caisse. J'ai travaillé en configuration légère — deux enceintes actives sur monotube pilotées par un contrôleur RX3 — avec de longues rallonges, parce que les points électriques sont dispersés : un côté forêt, un sous le chapiteau de pierre dans les lavandes. Point clé : j'oriente la diffusion vers les lavandes et la bâtisse, jamais vers l'extérieur du domaine. Le son reste plein pour les invités et ne part pas dans la vallée — c'est la meilleure assurance anti-plainte de voisinage sur ce lieu.",
    },
    expertise: {
      acoustique:
        "Espaces ouverts, sans mur de renvoi : le son se disperse vite. Je privilégie plusieurs points de diffusion rapprochés plutôt qu'un système poussé fort, et j'oriente systématiquement les enceintes vers les lavandes et la bâtisse pour contenir la diffusion.",
      logistique:
        "Pas d'accès véhicule jusqu'à la zone cérémonie / cocktail en contrebas : portage manuel intégral. Deux points électriques éloignés (côté forêt et sous le chapiteau de pierre) — je viens avec un jeu de rallonges longues et un contrôleur RX3 pour piloter la config.",
      moments_cles:
        "Cérémonie et cocktail en contrebas (config légère 2 enceintes actives sur monotube), dîner dans la cour autour de la fontaine, bal ensuite sur le système principal.",
    },
    caracteristiques: {
      espaces: [
        "Cour de la fontaine",
        "Chapiteau de pierre dans les lavandes",
        "Zone cérémonie en contrebas",
        "Pinède",
        "Bâtisse en pierre",
      ],
      style: "Domaine provençal, lavandes et pierre sèche",
      saison: "Juin à septembre — pleine floraison des lavandes en juillet",
    },
    conseils: [
      {
        titre: "Prévoir le temps de portage dans le planning",
        texte:
          "L'installation en contrebas se fait entièrement à pied. Je prévois systématiquement une marge d'installation plus large qu'ailleurs : c'est ce qui évite de courir avant l'arrivée des invités.",
      },
      {
        titre: "Orienter le son vers les lavandes et la bâtisse",
        texte:
          "C'est la règle que j'applique à Javon pour préserver la tranquillité alentour tout en gardant un niveau confortable côté invités. Cela évite d'avoir à baisser le volume en plein bal.",
      },
      {
        titre: "Anticiper les points électriques dispersés",
        texte:
          "Deux alimentations seulement, éloignées l'une de l'autre. Un DJ qui découvre le lieu le jour J se retrouve à court de câble — je viens avec le nécessaire.",
      },
    ],
    faq: [
      {
        q: "Où se trouve La Grange de Javon ?",
        a: "À Lioux (84220), dans le Vaucluse, au cœur du Luberon et au milieu des champs de lavande.",
      },
      {
        q: "Peut-on sonoriser la cérémonie en contrebas de la bâtisse ?",
        a: "Oui. J'y installe une configuration légère (deux enceintes actives sur monotube et contrôleur RX3) descendue à la main, avec des rallonges longues jusqu'aux points électriques du domaine.",
      },
      {
        q: "Y a-t-il des contraintes de bruit à Javon ?",
        a: "Le domaine est isolé, mais j'oriente toujours la diffusion vers les lavandes et la bâtisse plutôt que vers l'extérieur. Cela permet de garder un vrai niveau de fête sans gêner le voisinage.",
      },
    ],
    site: "https://www.lagrangedejavon.com",
    prestationsRealisees: "20+",
  },


  "le-petit-roulet": {
    slug: "le-petit-roulet",
    name: "Le Petit Roulet",
    shortName: "Le Petit Roulet",
    city: "Cavaillon",
    region: "Vaucluse",
    postalCode: "84300",
    metaTitle: "DJ mariage Le Petit Roulet, Cavaillon | DJ Val",
    metaDescription:
      "DJ référent au domaine Le Petit Roulet à Cavaillon. Acoustique de la salle, vue Luberon, dancefloor extérieur. 20 ans d'expérience locale. Devis sous 24 h.",
    hero: "/__l5e/assets-v1/55b202fa-0a0c-4d39-92b4-a04bb303e840/domaine-cavaillon.webp",
    heroAlt: "Mariage au Petit Roulet à Cavaillon, mise en lumière par DJ Val",
    intro:
      "Le Petit Roulet, c'est presque chez moi. À 10 minutes de mon studio à Cavaillon, ce domaine de plein pied avec vue dégagée sur le Luberon est l'un de ceux où j'ai animé le plus de mariages depuis 2015. Je connais chaque mètre carré, chaque prise électrique, chaque angle photo.",
    anecdote: {
      title: "Le mariage de mes voisins (et la pression qui va avec)",
      body:
        "Septembre 2020, mariage des fils du fleuriste de Cavaillon — autant dire que toute la ville était invitée et que je n'avais pas le droit à l'erreur. 160 invités, 11 h de prestation (cérémonie à 16 h, départ des derniers à 5 h du matin). Le matin, dans la voiture, je révisais mentalement la playlist mille fois. À la fin, le père du marié — 70 ans, jamais en boîte de sa vie — m'a serré dans ses bras et m'a dit « Val, j'ai jamais autant dansé ». Cette nuit-là, j'ai compris ce que ce métier voulait dire.",
    },
    expertise: {
      acoustique:
        "La salle principale est rectangulaire avec un plafond moyen — l'acoustique est très neutre, agréable à mixer. Pas besoin de réglages exotiques, on peut se concentrer sur la dynamique musicale.",
      logistique:
        "Parking devant la salle, déchargement en 15 minutes. Le domaine a deux compteurs 32 A indépendants — j'ai même branché un photobooth + machine à fumée + étincelles froides sans la moindre coupure.",
      moments_cles:
        "Cérémonie sur la pelouse vue Luberon, vin d'honneur sur la terrasse, dîner en salle, bal jusqu'à 5 h (lieu autonome côté voisinage, pas de contrainte stricte).",
    },
    caracteristiques: {
      capaciteAssis: "180 personnes",
      capaciteCocktail: "250 personnes",
      espaces: ["Salle de réception", "Terrasse couverte", "Pelouse cérémonie", "Parking dédié"],
      style: "Domaine moderne, vue Luberon",
      saison: "Toute l'année",
    },
    conseils: [
      {
        titre: "Profiter de la flexibilité horaire",
        texte:
          "C'est l'un des rares lieux du Vaucluse sans contrainte horaire stricte (voisinage éloigné). Vous pouvez danser jusqu'à 5 h sans stress.",
      },
      {
        titre: "Soigner la cérémonie face Luberon",
        texte:
          "L'orientation de la pelouse est plein sud — pour les cérémonies l'après-midi, je positionne le couple dos au Luberon pour que la lumière ne les éblouisse pas et que les photos soient parfaites.",
      },
    ],
    faq: [
      {
        q: "Y a-t-il un limiteur sonore au Petit Roulet ?",
        a: "Non, pas de limiteur installé. Le lieu est suffisamment isolé pour permettre une vraie soirée sans contrainte de niveau sonore.",
      },
      {
        q: "Jusqu'à quelle heure peut-on danser ?",
        a: "Jusqu'à 5 h du matin sans problème — j'ai déjà fait des prestations qui se sont terminées à 5 h 30. Le domaine reste accessible le lendemain matin pour le brunch.",
      },
      {
        q: "Combien de mariages avez-vous animés au Petit Roulet ?",
        a: "Plus de 30 prestations entre 2015 et aujourd'hui. C'est mon lieu le plus fréquenté avec Malaugo.",
      },
    ],
    site: "https://www.lepetitroulet-provence.com",
    prestationsRealisees: "30+",
  },

  "domaine-de-manville": {
    slug: "domaine-de-manville",
    name: "Domaine de Manville",
    shortName: "Manville",
    city: "Les Baux-de-Provence",
    region: "Alpilles — Bouches-du-Rhône",
    postalCode: "13520",
    metaTitle: "DJ mariage Domaine de Manville, Les Baux | DJ Val",
    metaDescription:
      "DJ mariage au Domaine de Manville aux Baux-de-Provence. 5 étoiles, golf & spa, 720 m² de salles de réception. Sonorisation cérémonie, soirée lounge et dancefloor. Devis 24 h.",
    hero: "/images/domaine-de-manville-hero.jpg",
    heroAlt: "Réception de mariage en extérieur au Domaine de Manville aux Baux-de-Provence au coucher du soleil, ambiance signée DJ Val",
    intro:
      "Le Domaine de Manville, c'est l'Alliance parfaite entre le prestige d'un palace 5* et l'âme authentique d'une bastide des Alpilles. Posé aux portes du village des Baux-de-Provence, ce domaine conjugue pierres dorées, jardins méditerranéens, golf et spa, pour des mariages où le luxe se fait discret et intemporel. J'y interviens régulièrement et chaque prestation y est un équilibre entre élégance et fête.",
    anecdote: {
      title: "Le mariage où la piste a pris place sur la terrasse",
      body:
        "Juin 2024, mariage de S. et T., 110 invités, dîner dans la salle principale puis ouverture de bal sur la terrasse surplombant le golf. Le défi : passer d'une ambiance feutrée à une soirée dansante sans dénaturer le cadre. J'ai installé une sono compacte RCF en façade de terrasse, calibré la lumière sur les oliviers pour un effet château, et lancé la première danse au moment exact où le soleil touchait les Alpilles. Résultat : 90 invités sur la piste à minuit, et le directeur de la réception est venu me demander ma carte pour ses futurs mariés.",
    },
    expertise: {
      acoustique:
        "Les salles du Domaine de Manville sont spacieuses et hautes de plafond, avec des matériaux nobles (pierre, bois, tissus) qui absorbent bien les hautes fréquences. Je travaille avec une diffusion en colonnes sur la périphérie pour homogénéiser le son sans écraser les conversations au dîner.",
      logistique:
        "Accès par l'avenue du Golf, déchargement possible à proximité immédiate de la salle de réception. Le domaine dispose d'une alimentation électrique hôtelière fiable ; je préviens toujours la réceptioniste pour réserver le bon emplacement de branchement.",
      moments_cles:
        "Cocktail dans les jardins méditerranéens (jazz/lounge), dîner en salle avec musique feutrée, ouverture de bal sur la terrasse au coucher du soleil, puis dancefloor jusqu'à 2 h ou 3 h selon le forfait.",
    },
    caracteristiques: {
      capaciteAssis: "150 personnes",
      capaciteCocktail: "200 personnes",
      espaces: [
        "Salle de réception 720 m²",
        "Terrasses sur le golf",
        "Jardins méditerranéens",
        "Restaurant l'Aupiho ✽",
        "Spa & Golf 18 trous",
      ],
      style: "Bastide 5*, luxe discret et contemporain",
      saison: "Avril à octobre (idéal), possible toute l'année",
    },
    conseils: [
      {
        titre: "Profiter de la terrasse pour l'ouverture de bal",
        texte:
          "La terrasse surplombant le golf offre une lumière magique entre 20 h 30 et 21 h 30. C'est le moment idéal pour une ouverture de bal mémorable, avant de basculer en salle pour le dancefloor.",
      },
      {
        titre: "Prévoir une cérémonie laïque dans le jardin",
        texte:
          "Le jardin méditerranéen, abrité par les bâtiments de la bastide, est parfait pour une cérémonie en extérieur. Je recommande deux enceintes Bose S1 Pro sur pied et deux micros HF Shure pour une voix claire sans encombrer les photos.",
      },
    ],
    faq: [
      {
        q: "Le Domaine de Manville impose-t-il des prestataires exclusifs ?",
        a: "Non, vous êtes libre de choisir votre DJ. Le domaine demande simplement les documents d'assurance (RC Pro) et le respect du règlement intérieur. Je fournis systématiquement mon attestation d'assurance Hiscox et mon relevé d'identité bancaire SACEM/SPRÉ.",
      },
      {
        q: "Quelle est la capacité maximale pour un mariage au Domaine de Manville ?",
        a: "Les espaces de réception totalisent 720 m² et peuvent accueillir environ 150 personnes assises ou 200 en cocktail debout. Le domaine propose plusieurs configurations selon le format de votre réception.",
      },
      {
        q: "Jusqu'à quelle heure peut-on danser au Domaine de Manville ?",
        a: "En général, la musique extérieure s'arrête à 23 h et le bal intérieur peut se poursuivre jusqu'à 2 h ou 3 h selon l'accord passé avec le domaine. Je m'adapte à chaque contrat pour éviter toute interruption brutale.",
      },
      {
        q: "Quel est votre tarif pour animer un mariage au Domaine de Manville ?",
        a: "Comptez entre 1 890 € et 2 800 € selon la durée, la sonorisation de la cérémonie, le pack lumière architectural et les options (photobooth, étincelles froides, DJ set lounge supplémentaire). Devis personnalisé sous 24 h.",
      },
      {
        q: "Connaissez-vous bien le Domaine de Manville ?",
        a: "J'y ai animé une dizaine de mariages et soirées privées. Je connais les équipes de réception, les contraintes électriques, les bons emplacements pour chaque moment de la journée, et les traiteurs habituels du lieu.",
      },
    ],
    site: "https://www.domainedemanville.fr",
    geo: { lat: 43.732311, lng: 4.802083 },
    prestationsRealisees: "10+",
  },
};

export const VENUE_SLUGS = Object.keys(VENUES);
export const VENUE_LIST: Venue[] = Object.values(VENUES);
